import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isVoiceInputSupported } from "./audioCapture";
import { type DictationSession, type DictationStatus, startDictationSession } from "./dictationSession";
import type { DictationError } from "./errors";
import { joinDictation } from "./transcript";

export interface UseDictationOptions {
  /** The field's text. */
  value: string;
  onChange: (value: string) => void;
  /** A new value (e.g. another chat) cancels dictation; nothing more is written. */
  resetKey: string | number;
  onError: (error: DictationError) => void;
}

interface Merge {
  /** The text before the first dictated words; null until they arrive. */
  base: string | null;
  /** What dictation wrote last. */
  written: string | null;
  /** The user edited the text, so dictation no longer writes. */
  detached: boolean;
}

const FRESH: Merge = { base: null, written: null, detached: false };

/**
 * Live dictation into a controlled text field. The words go after whatever the
 * field held when they started arriving. If the user edits the text meanwhile,
 * dictation stops and leaves their edit alone.
 */
export function useDictation({ value, onChange, resetKey, onError }: UseDictationOptions) {
  const [status, setStatus] = useState<DictationStatus>("idle");
  const statusRef = useRef<DictationStatus>("idle");
  const session = useRef<DictationSession | null>(null);
  const merge = useRef<Merge>(FRESH);
  const latest = useRef({ value, onChange, onError });
  useLayoutEffect(() => {
    latest.current = { value, onChange, onError };
    const m = merge.current;
    // The user edited what dictation wrote: stop now, and keep their edit.
    if (statusRef.current !== "idle" && m.written !== null && !m.detached && value !== m.written) {
      m.detached = true;
      session.current?.stop();
    }
  });

  const write = useCallback((text: string) => {
    const m = merge.current;
    if (m.detached) return;
    m.base ??= latest.current.value;
    m.written = joinDictation(m.base, text);
    latest.current.onChange(m.written);
  }, []);

  const toggle = useCallback(() => {
    if (statusRef.current === "recording") {
      session.current?.stop();
      return;
    }
    if (statusRef.current !== "idle") return;
    if (!isVoiceInputSupported()) {
      latest.current.onError("unsupported");
      return;
    }
    merge.current = { ...FRESH };
    try {
      session.current = startDictationSession({
        onStatus: (next) => {
          statusRef.current = next;
          setStatus(next);
        },
        onText: write,
        onError: (error) => latest.current.onError(error),
      });
    } catch {
      latest.current.onError("failed");
    }
  }, [write]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the trigger
  useEffect(() => () => session.current?.cancel(), [resetKey]);

  return { status, toggle };
}
